import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { getPostBySlug } from './posts';
import { BlogNav, BlogFooter } from './Blog';
import './blog.css';

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const buildPostLd = (post) =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    datePublished: post.date,
    dateModified: post.date,
    author: { '@type': 'Person', name: post.author },
    publisher: {
      '@type': 'Organization',
      name: 'Groupardy',
      url: 'https://groupardy.com/',
      logo: { '@type': 'ImageObject', url: 'https://groupardy.com/g2-logo.png' },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://groupardy.com/blog/${post.slug}`,
    },
    url: `https://groupardy.com/blog/${post.slug}`,
    description: post.excerpt,
    image: 'https://groupardy.com/og-image.jpg',
  });

const initialsFor = (name) =>
  name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────
const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="blog">
        <Helmet>
          <title>Post not found · Groupardy Blog</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <BlogNav />
        <main className="bl-empty">
          <div className="bl-wrap">
            <h2>That post doesn't exist.</h2>
            <p>It might have moved, or the link might be off by a letter.</p>
            <Link to="/blog" className="bl-btn bl-btn-primary bl-btn-lg">
              Back to the blog
            </Link>
          </div>
        </main>
        <BlogFooter />
      </div>
    );
  }

  const canonical = `https://groupardy.com/blog/${post.slug}`;

  return (
    <div className="blog">
      <Helmet>
        <title>{`${post.title} · Groupardy Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <meta name="author" content={post.author} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:site_name" content="Groupardy" />
        <meta property="og:image" content="https://groupardy.com/og-image.jpg" />
        <meta property="article:published_time" content={`${post.date}T00:00:00Z`} />
        <meta property="article:author" content={post.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="theme-color" content="#2ec4b6" />
        <script type="application/ld+json">{buildPostLd(post)}</script>
      </Helmet>

      <BlogNav />

      <main className="bl-post">
        <article className="bl-wrap bl-post-inner">
          <Link to="/blog" className="bl-back-link">All posts</Link>

          <header className="bl-post-head">
            <div className="bl-post-meta">
              <span>{formatDate(post.date)}</span>
              {post.readMinutes && (
                <>
                  <span className="bl-dot" />
                  <span>{post.readMinutes} min read</span>
                </>
              )}
            </div>
            <h1>{post.title}</h1>
            <div className="bl-post-byline">
              <div className="bl-byline-avatar" aria-hidden="true">
                {initialsFor(post.author)}
              </div>
              <div className="bl-byline-meta">
                <div className="bl-byline-name">{post.author}</div>
                {post.authorRole && (
                  <div className="bl-byline-role">{post.authorRole}</div>
                )}
              </div>
            </div>
          </header>

          <div className="bl-post-body">{post.body}</div>

          <aside className="bl-post-cta">
            <div className="bl-post-cta-text">
              <strong>Try it</strong>
              Build a board for your group in about ninety seconds. Free to start.
            </div>
            <a className="bl-btn bl-btn-primary bl-btn-lg" href="/create">
              Start a game
            </a>
          </aside>
        </article>
      </main>

      <BlogFooter />
    </div>
  );
};

export default BlogPost;
